import { Box, Stack, useTheme } from "@mui/material";
import { Drawer } from "components";
import { useCallback, useState } from "react";

interface HtmlCodeBlockModalProps {
  open: boolean;
  onClose: () => void;
  code: string;
  onSave: (code: string) => void;
  onDelete: () => void;
}

/**
 * Modal (Drawer) for editing raw HTML code within an HTML code block.
 * Uses the project's Drawer component per coding standards.
 * Uses a simple textarea for code editing.
 */
export const HtmlCodeBlockModal = ({
  open,
  onClose,
  code,
  onSave,
  onDelete,
}: HtmlCodeBlockModalProps): JSX.Element => {
  const theme = useTheme();
  const [htmlCode, setHtmlCode] = useState(code);

  const handleSave = useCallback(() => {
    onSave(htmlCode);
  }, [htmlCode, onSave]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setHtmlCode(e.target.value);
    },
    [],
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={code ? "Edit HTML code" : "Insert HTML code"}
      primaryButtonText="Save"
      secondaryButtonText={code ? "Delete this block" : "Cancel"}
      onPrimaryButtonClick={handleSave}
      onSecondaryButtonClick={code ? onDelete : onClose}
      isPrimaryButtonDisabled={htmlCode.length === 0}
      sx={{
        "& .MuiModal-paper": {
          width: "600px",
          height: "min(650px, 85vh)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box sx={{ p: "16px", width: "900px", height: "100%" }}>
        <Stack
          sx={{
            position: "relative",
            width: "100%",
            height: "100%",
            overflow: "hidden",
            p: "8px",
            backgroundColor: theme.palette.neutral["005"],
            borderRadius: "8px",
            border: `1px solid ${theme.palette.neutral["010"]}`,
          }}
        >
          <Box
            component="textarea"
            value={htmlCode}
            onChange={handleChange}
            placeholder="Enter your HTML code here"
            spellCheck={false}
            sx={{
              width: "100%",
              height: "100%",
              resize: "none",
              border: "none",
              outline: "none",
              backgroundColor: "transparent",
              fontFamily:
                "'Fira Code', 'Monaco', 'Consolas', 'Courier New', monospace",
              fontSize: "12px",
              lineHeight: "19px",
              color: theme.palette.lightBg.high,
              "&::placeholder": {
                color: theme.palette.lightBg.low,
              },
            }}
          />
        </Stack>
      </Box>
    </Drawer>
  );
};
